import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  MapPin, 
  Star, 
  Edit2, 
  Trash2,
  Eye,
  Loader2,
  Car
} from "lucide-react";

import { API } from "../utils/api";

export default function MySpots() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [spotToDelete, setSpotToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchMySpots();
    }
  }, [user]);

  const fetchMySpots = async () => {
    try {
      const response = await axios.get(`${API}/spots/my`);
      setSpots(response.data);
    } catch (error) {
      console.error("Error fetching spots:", error);
      toast.error("Error al cargar tus plazas");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (spot) => {
    setSpotToDelete(spot);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!spotToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/spots/${spotToDelete.id}`);
      setSpots(spots.filter(s => s.id !== spotToDelete.id));
      toast.success("Plaza eliminada correctamente");
      setDeleteModalOpen(false);
      setSpotToDelete(null);
    } catch (error) {
      toast.error("Error al eliminar la plaza");
    } finally {
      setDeleting(false);
    }
  };

  const toggleAvailability = async (spot) => {
    try {
      await axios.put(`${API}/spots/${spot.id}`, {
        is_available: !spot.is_available
      });
      setSpots(spots.map(s => 
        s.id === spot.id ? { ...s, is_available: !s.is_available } : s
      ));
      toast.success(spot.is_available ? "Plaza marcada como no disponible" : "Plaza marcada como disponible");
    } catch (error) {
      toast.error("Error al actualizar disponibilidad");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="pt-20 min-h-screen" data-testid="my-spots-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Mis Plazas</h1>
            <p className="text-gray-400">Gestiona tus plazas de garaje publicadas</p>
          </div>
          <Link to="/add-spot">
            <Button className="bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover" data-testid="add-new-spot-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nueva Plaza
            </Button>
          </Link>
        </div>

        {/* Spots List */}
        {spots.length > 0 ? (
          <div className="space-y-4">
            {spots.map((spot) => (
              <div 
                key={spot.id}
                className="dashboard-card flex flex-col sm:flex-row gap-6"
                data-testid={`my-spot-${spot.id}`}
              >
                {/* Image */}
                <div className="w-full sm:w-48 h-32 sm:h-auto rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={spot.images?.[0] || "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"}
                    alt={spot.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h3 className="text-xl font-semibold text-white">{spot.title}</h3>
                      <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{spot.address}, {spot.city}</span>
                      </div>
                    </div>
                    <Badge className={spot.is_available ? "badge-available" : "badge-occupied"}>
                      {spot.is_available ? "Disponible" : "No disponible"}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 mb-4">
                    {spot.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="text-white">{spot.rating.toFixed(1)}</span>
                        <span className="text-gray-500">({spot.review_count})</span>
                      </div>
                    )}
                    <div className="price-display text-white">
                      €{spot.price_hour.toFixed(2)}/h
                    </div>
                    {spot.price_month && (
                      <div className="text-gray-400">
                        €{spot.price_month.toFixed(0)}/mes
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {spot.features?.slice(0, 4).map((feature, idx) => (
                      <span key={idx} className="feature-badge">{feature}</span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/spot/${spot.id}`}>
                      <Button variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5">
                        <Eye className="w-4 h-4 mr-2" />
                        Ver
                      </Button>
                    </Link>
                    <Link to={`/edit-spot/${spot.id}`}>
                      <Button variant="outline" size="sm" className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10">
                        <Edit2 className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-white/10 text-white hover:bg-white/5"
                      onClick={() => toggleAvailability(spot)}
                      data-testid={`toggle-availability-${spot.id}`}
                    >
                      {spot.is_available ? "Desactivar" : "Activar"}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDeleteClick(spot)}
                      data-testid={`delete-spot-${spot.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state dashboard-card">
            <div className="empty-state-icon">
              <Car className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No tienes plazas publicadas</h3>
            <p className="text-gray-400 mb-6">
              Empieza a ganar dinero publicando tu primera plaza de garaje
            </p>
            <Link to="/add-spot">
              <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                <Plus className="w-5 h-5 mr-2" />
                Publicar Plaza
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Eliminar Plaza</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400">
            ¿Estás seguro de que quieres eliminar "{spotToDelete?.title}"? Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
